import type { OsmPointOfInterest } from './osmClient';

export const geoManifestUrl = 'https://storage.googleapis.com/doddledays-1536605218990-geo/latest/manifest.json';
const manifestTtlMs = 5 * 60_000;

export type GeoManifest = {
  version: string;
  generated_at: string;
  regions: string[];
  basemap: string;
  pois: string;
  map_version?: string;
  map_generated_at?: string;
  routing?: string;
  routing_api?: string;
  routing_image?: string;
  routing_version?: string;
  routing_generated_at?: string;
};

type ManifestCache = { at: number; value: GeoManifest | null };
let manifestCache: ManifestCache | null = null;
let manifestRequest: Promise<GeoManifest | null> | null = null;

function validManifest(value: unknown): value is GeoManifest {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<GeoManifest>;
  return typeof candidate.version === 'string'
    && typeof candidate.basemap === 'string'
    && candidate.basemap.startsWith('https://')
    && typeof candidate.pois === 'string'
    && candidate.pois.startsWith('https://');
}

export async function loadGeoManifest(force = false): Promise<GeoManifest | null> {
  const now = Date.now();
  if (!force && manifestCache && now - manifestCache.at < manifestTtlMs) return manifestCache.value;
  if (!force && manifestRequest) return manifestRequest;

  manifestRequest = (async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 2_500);
    try {
      const response = await fetch(geoManifestUrl, {
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      if (!response.ok) return null;
      const value = await response.json() as unknown;
      return validManifest(value) ? value : null;
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  })();

  try {
    const value = await manifestRequest;
    manifestCache = { at: Date.now(), value };
    return value;
  } finally {
    manifestRequest = null;
  }
}

function stringProperty(properties: Record<string, unknown>, key: string): string | undefined {
  const value = properties[key];
  if (typeof value === 'string' && value.length > 0) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
}

export function selfHostedPoiFromMapFeature(feature: {
  id?: string | number;
  properties?: Record<string, unknown> | null;
  geometry?: { type?: string; coordinates?: unknown } | null;
}): OsmPointOfInterest | null {
  const properties = feature.properties ?? {};
  const geometry = feature.geometry;
  if (geometry?.type !== 'Point' || !Array.isArray(geometry.coordinates)) return null;
  const [longitude, latitude] = geometry.coordinates;
  if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return null;

  const category = stringProperty(properties, 'category')
    ?? stringProperty(properties, 'amenity')
    ?? stringProperty(properties, 'tourism')
    ?? stringProperty(properties, 'leisure')
    ?? stringProperty(properties, 'shop')
    ?? stringProperty(properties, 'historic')
    ?? stringProperty(properties, 'natural')
    ?? stringProperty(properties, 'public_transport')
    ?? (stringProperty(properties, 'changing_table') ? 'baby_change' : 'poi');
  const osmId = stringProperty(properties, 'osm_id') ?? String(feature.id ?? `${latitude},${longitude}`);
  const osmType = stringProperty(properties, 'osm_type') ?? 'node';
  const name = stringProperty(properties, 'name') ?? category.replaceAll('_', ' ');
  const tags: Record<string, string> = {};
  const tagMapping: Record<string, string> = {
    amenity: 'amenity',
    leisure: 'leisure',
    tourism: 'tourism',
    shop: 'shop',
    historic: 'historic',
    natural: 'natural',
    public_transport: 'public_transport',
    changing_table: 'changing_table',
    baby_changing: 'baby_changing',
    website: 'website',
    contact_website: 'contact:website',
    opening_hours: 'opening_hours',
    wheelchair: 'wheelchair',
    fee: 'fee',
    access: 'access',
    phone: 'phone',
    contact_phone: 'contact:phone',
    wikipedia: 'wikipedia',
    wikidata: 'wikidata',
    operator: 'operator',
    description: 'description',
    addr_street: 'addr:street',
    addr_housenumber: 'addr:housenumber',
    addr_city: 'addr:city',
  };
  for (const [propertyKey, tagKey] of Object.entries(tagMapping)) {
    const value = stringProperty(properties, propertyKey);
    if (value) tags[tagKey] = value;
  }
  tags.name = name;

  return {
    id: `osm-poi-${osmType}-${osmId}`,
    name,
    category,
    latitude: latitude as number,
    longitude: longitude as number,
    tags,
  };
}
